import React, { useMemo } from "react";
import PublicHeader from "../components/homepage/public/PublicHeader";
import PublicFooter from "../components/homepage/public/PublicFooter";

const IMG_BASE = "/images/tutoring/sat";

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function Img({ src, alt, className, style }) {
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    setHasError(false);
  }, [src]);

  if (hasError) {
    return (
      <div
        className={className}
        style={{
          ...style,
          background: "linear-gradient(135deg, rgba(29,78,216,0.10), rgba(2,6,23,0.06))",
        }}
        aria-label={`Fallback for ${alt}`}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
}

export default function SATTutoring() {
  const css = useMemo(
    () => `
      :root{
        --ink:#0f172a;
        --muted:#64748b;
        --line:#e2e8f0;
        --cardShadow: 0 22px 60px rgba(15,23,42,0.10);
        --miniShadow: 0 16px 40px rgba(15,23,42,0.06);
        --accentA:#1d4ed8;
        --accentB:#0b1530;
      }

      .satPage { font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif; color: var(--ink); background: #fff; overflow-x: hidden; }
      .serif { font-family: 'Playfair Display', serif; }
      .container { max-width: 1100px; margin: 0 auto; padding: 0 20px; }
      .muted { color: var(--muted); }
      .uppercase { text-transform: uppercase; letter-spacing: 0.05em; }

      .section { padding: 90px 0; position: relative; }
      .sectionAlt { padding: 90px 0; background: #f8fafc; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); position: relative; }

      .bgGlow::before{ content:""; position:absolute; inset:0; pointer-events:none; z-index:0; }
      .bgGlow2::before{
        content:""; position:absolute; inset:auto -200px -240px -200px; height: 520px;
        background: radial-gradient(circle at 78% 60%, rgba(29,78,216,0.14), transparent 60%),
                    radial-gradient(circle at 22% 70%, rgba(2,6,23,0.08), transparent 62%);
        pointer-events:none; z-index:0;
      }
      .layer { position: relative; z-index: 1; }

      .grid2 { display:grid; grid-template-columns: minmax(0,1.1fr) minmax(0,0.9fr); gap: 34px; align-items: center; }
      .card { border-radius: 24px; border: 1px solid var(--line); background: #fff; box-shadow: var(--cardShadow); overflow: hidden; }
      .img { width: 100%; height: 100%; display:block; object-fit: cover; }

      .pill {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 7px 12px; border-radius: 999px;
        border: 1px solid var(--line);
        background: rgba(255,255,255,0.8);
        font-size: 12px; font-weight: 800;
      }

      .btnRow { display:flex; gap: 10px; flex-wrap: wrap; margin-top: 18px; }
      .btn {
        font-size: 13px; padding: 10px 18px; border-radius: 999px;
        border: 1px solid #d0d7e2; background: #fff;
        cursor: pointer; font-weight: 900;
        transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
      }
      .btn:hover { transform: translateY(-1px); box-shadow: 0 14px 26px rgba(15,23,42,0.10); }
      .btnPrimary {
        background: linear-gradient(135deg, var(--accentA), var(--accentB));
        color: #fff; border-color: transparent;
        box-shadow: 0 14px 30px rgba(15,23,42,0.18);
      }
      .btnPrimary:hover { box-shadow: 0 18px 40px rgba(15,23,42,0.22); }

      /* HERO */
      .hero {
        padding: 66px 0;
        background-image: url('${IMG_BASE}/hero-bg-custom.jpg'); 
        background-size: cover;
        background-position: center top;
        background-repeat: no-repeat;
        border-bottom: 1px solid var(--line);
        position: relative;
      }
      .heroH1 { font-size: 52px; line-height: 1.02; margin: 10px 0 0; color: #020617; letter-spacing: -0.02em; }
      .heroLead { margin-top: 14px; font-size: 15px; line-height: 1.9; color: #475569; }

      .heroCardTop{
        padding: 14px 16px;
        display:flex; align-items:center; justify-content: space-between; gap: 10px;
        background: linear-gradient(90deg, rgba(29,78,216,0.10), rgba(2,6,23,0.03));
        border-bottom: 1px solid rgba(226,232,240,0.8);
      }
      .heroTag{ font-size: 12px; font-weight: 900; color: #0f172a; }
      .heroTag span{ color: var(--muted); font-weight: 800; margin-left: 10px; }
      .heroMedia { height: 360px; }
      .heroMediaFallback{
        height: 360px;
        background: radial-gradient(circle at 25% 30%, rgba(29,78,216,0.25), transparent 55%),
                    linear-gradient(135deg, rgba(15,23,42,0.06), rgba(29,78,216,0.06));
      }

      /* --- NEW: LARGE CARD STYLES (Benefits & Qualities) --- */
      .bigGrid {
        display:grid; grid-template-columns: repeat(3, minmax(0,1fr));
        gap: 20px; justify-content: center; margin-top: 30px;
      }
      .bigCard {
        border-radius: 24px;
        border: 1px solid var(--line);
        background: rgba(255,255,255,0.95);
        box-shadow: var(--miniShadow);
        padding: 30px 24px; /* Larger padding */
        text-align: center;
        transition: transform .15s ease, box-shadow .15s ease;
      }
      .bigCard:hover { transform: translateY(-4px); box-shadow: 0 25px 50px rgba(15,23,42,0.12); }
      
      .bigIcon {
        width: 72px; height: 72px; /* Increased from 46px */
        margin: 0 auto 18px auto;
        object-fit: contain;
        display:block;
        border-radius: 16px;
        background: #fff;
        border: 1px solid rgba(226,232,240,0.8);
        padding: 12px;
        box-shadow: 0 12px 24px rgba(15,23,42,0.08);
      }
      .bigTitle { font-size: 19px; font-weight: 950; margin: 0; color: #0f172a; letter-spacing: -0.01em; }
      .bigText { margin: 10px 0 0; font-size: 15px; line-height: 1.7; color: var(--muted); }


      /* --- STANDARD MINI CARD (kept for small English/Math features) --- */
      .miniCard {
        border-radius: 20px; border: 1px solid var(--line);
        background: rgba(255,255,255,0.88);
        box-shadow: var(--miniShadow);
        padding: 18px; text-align: center;
      }
      .miniIconImg{
        width: 46px; height: 46px; border-radius: 14px;
        border: 1px solid rgba(226,232,240,0.9); background: #fff;
        display:block; object-fit: contain; padding: 8px;
        box-shadow: 0 10px 20px rgba(15,23,42,0.06);
        margin: 0 auto 12px auto;
      }
      .miniTitle { font-size: 14px; font-weight: 950; margin: 0; }
      .miniText { margin: 8px 0 0; font-size: 13px; line-height: 1.75; color: var(--muted); }

      /* --- NEW: RESULTS CARD (For actual images) --- */
      .resultCard {
        border-radius: 24px; border: 1px solid var(--line);
        background: #fff; box-shadow: var(--cardShadow);
        padding: 24px; text-align: center;
        display: flex; flex-direction: column;
      }
      .resultImgFrame {
        width: 100%; aspect-ratio: 16/10; /* Rectangular for screenshots */
        background: #f1f5f9; border-radius: 16px;
        overflow: hidden; margin-bottom: 20px;
        border: 1px solid var(--line);
      }
      .resultImg { width: 100%; height: 100%; object-fit: cover; }
      .resultTitle { font-size: 20px; font-weight: 950; margin: 0; color: #0f172a; }
      .resultText { margin: 10px 0 0; font-size: 15px; line-height: 1.6; color: var(--muted); }


      /* --- CURRICULUM LAYOUT --- */
      .currContainer {
        display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 40px;
        text-align: left;
      }
      .currCol {
        background: #fff; border: 1px solid var(--line);
        border-radius: 24px; padding: 30px;
        box-shadow: var(--miniShadow);
      }
      .currHeader {
        font-size: 24px; font-weight: 900; color: #020617;
        margin-bottom: 24px; padding-bottom: 16px;
        border-bottom: 2px solid var(--accentA);
        text-align: center;
        letter-spacing: -0.02em;
      }
      .currSectionTitle {
        font-size: 14px; font-weight: 900; 
        color: var(--accentA); text-transform: uppercase; letter-spacing: 0.05em;
        margin: 20px 0 10px;
      }
      .currList { padding: 0; margin: 0; list-style: none; }
      .currItem {
        padding: 6px 0; border-bottom: 1px solid #f1f5f9;
        font-size: 15px; color: #334155; font-weight: 500;
        display: flex; align-items: start; gap: 10px;
      }
      .currItem::before {
        content: "•"; color: var(--accentA); font-weight: bold;
      }

      .centerBlock { text-align:center; max-width: 820px; margin: 0 auto; }
      .divider { height: 1px; background: var(--line); margin: 0; border: none; }

      /* Centered Quotes */
      .quote {
        border-radius: 24px; border: 1px solid var(--line);
        background: #fff; padding: 40px;
        box-shadow: 0 18px 45px rgba(15,23,42,0.08);
        position: relative; overflow: hidden;
        text-align: center; /* Centers the content */
      }
      .quoteText { font-size: 22px; line-height: 1.6; margin: 0; font-style: italic; color: #0f172a; }
      .quoteBy { 
        margin-top: 18px; font-size: 13px; letter-spacing: 0.12em; 
        text-transform: uppercase; color: var(--accentA); font-weight: 900; 
        display: block; /* Ensures centering works */
      }

      .audience { display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 16px; margin-top: 26px; }
      .audCard { border-radius: 20px; border: 1px solid var(--line); background: #fff; padding: 22px; box-shadow: var(--miniShadow); display:flex; flex-direction: column; gap: 10px; }
      .audTitle { margin: 0; font-size: 18px; font-weight: 950; }

      /* Standard 3-grid (for English/Math features) */
      .three { 
        display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); 
        gap: 16px; margin-top: 24px; justify-content: center;
      }

      .timelineWrap{
        margin-top: 30px; position: relative; border-radius: 26px;
        border: 1px solid var(--line); background: linear-gradient(180deg, #ffffff, #f8fafc);
        box-shadow: var(--cardShadow); overflow: hidden;
      }
      .timelineHeader{
        padding: 18px 18px 16px; border-bottom: 1px solid rgba(226,232,240,0.9);
        background: radial-gradient(circle at 20% 0%, rgba(29,78,216,0.12), transparent 55%);
        display:flex; justify-content: space-between; align-items: flex-end; gap: 14px; flex-wrap: wrap;
      }
      .timelineGrid{ display:grid; grid-template-columns: 1fr 1fr; gap: 0; }
      .timelineCol{ padding: 18px; position: relative; }
      .timelineCol:first-child{ border-right: 1px solid rgba(226,232,240,0.9); }
      .timelineLine{
        position:absolute; left: 30px; top: 22px; bottom: 22px; width: 3px;
        background: linear-gradient(180deg, rgba(29,78,216,0.55), rgba(2,6,23,0.10));
        border-radius: 999px; opacity: .9;
      }
      .step{ position: relative; padding-left: 66px; margin: 14px 0; }
      .dot{
        position:absolute; left: 20px; top: 2px; width: 22px; height: 22px;
        border-radius: 999px; background: #fff; border: 3px solid rgba(29,78,216,0.45);
        box-shadow: 0 10px 18px rgba(15,23,42,0.10);
      }
      .stepCard{
        border-radius: 18px; border: 1px solid rgba(226,232,240,0.95); background: #fff;
        padding: 14px 14px 13px; box-shadow: var(--miniShadow);
        display:flex; gap: 12px; align-items: flex-start;
      }
      .stepIcon{ width: 44px; height: 44px; object-fit: contain; }
      .stepH{ font-weight: 950; margin: 0; font-size: 14px; }
      .stepD{ margin: 6px 0 0; font-size: 13px; color: var(--muted); line-height: 1.75; }

      .ctaBanner {
        position: relative; border-radius: 28px; border: 1px solid var(--line); overflow:hidden;
        background: radial-gradient(circle at top, rgba(29,78,216,0.12), rgba(255,255,255,1) 62%);
        box-shadow: var(--cardShadow);
      }
      .ctaBg { position:absolute; inset:0; opacity:0.14; background-image: url('${IMG_BASE}/cta-bg.jpg'); background-size: cover; background-position: center; }
      .ctaInner { padding: 48px 22px; text-align:center; position: relative; z-index: 1; }

      @media (max-width: 980px) {
        .grid2 { grid-template-columns: 1fr; }
        .benefits { grid-template-columns: repeat(2, minmax(0,1fr)); }
        .bigGrid { grid-template-columns: 1fr; } /* Stack large cards on mobile */
        .currContainer { grid-template-columns: 1fr; }
        .three { grid-template-columns: 1fr; }
        .timelineGrid{ grid-template-columns: 1fr; }
        .timelineCol:first-child{ border-right: none; border-bottom: 1px solid rgba(226,232,240,0.9); }
        .heroH1 { font-size: 44px; }
      }
    `,
    []
  );

  const benefits = [
    {
      img: `${IMG_BASE}/benefit-high-yield.png`,
      title: "High-yield",
      text: "We focus on the exact skills that move your score—no wasted time.",
    },
    {
      img: `${IMG_BASE}/benefit-interactive.png`,
      title: "Interactive practice",
      text: "Targeted drills, strategy coaching, and realistic timed sets.",
    },
    {
      img: `${IMG_BASE}/benefit-tracking.png`,
      title: "Progress tracking",
      text: "Clear skill tracking and weekly checkpoints to stay on track.",
    },
    {
      img: `${IMG_BASE}/benefit-clear.png`,
      title: "Simple & clear",
      text: "Parents always know what’s happening and what’s next.",
    },
  ];

  // Specific detailed curriculum data
  const curriculumData = {
    rw: [
      {
        category: "Information and Ideas",
        items: ["Central Ideas and Details", "Inferences", "Command of Evidence"]
      },
      {
        category: "Craft and Structure",
        items: ["Words in Context", "Text Structure and Purpose", "Cross-Text Connections"]
      },
      {
        category: "Expression of Ideas",
        items: ["Rhetorical Synthesis", "Transitions"]
      },
      {
        category: "Standard English Conventions",
        items: ["Boundaries", "Form, Structure, and Sense"]
      }
    ],
    math: [
      {
        category: "Algebra",
        items: [
          "Linear equations in one variable",
          "Linear equations in two variables",
          "Linear functions",
          "Systems of two linear equations in two variables",
          "Linear inequalities in one or two variables"
        ]
      },
      {
        category: "Advanced Math",
        items: [
          "Equivalent expressions",
          "Nonlinear equations in one variable and systems",
          "Nonlinear functions",
        ]
      },
      {
        category: "Problem-Solving and Data Analysis",
        items: [
          "Ratios, rates, proportional relationships, and units",
          "Percentages",
          "One-variable data - Distributions & measures",
          "Two-variable data - Models and scatterplots",
          "Probability and conditional probability",
          "Inference from sample statistics and margin of error",
          "Evaluating statistical claims"
        ]
      },
      {
        category: "Geometry and Trigonometry",
        items: [
          "Area and volume",
          "Lines, angles, and triangles",
          "Right triangles and trigonometry",
          "Circles"
        ]
      }
    ]
  };

  const englishFeatures = [
    { img: `${IMG_BASE}/rw-reading.png`, title: "Reading strategies", text: "Short passages, quick inference, and evidence selection." },
    { img: `${IMG_BASE}/rw-grammar.png`, title: "Grammar patterns", text: "High-frequency rules taught as patterns, not memorization." },
    { img: `${IMG_BASE}/rw-time.png`, title: "Time management", text: "Pacing checkpoints so students finish confidently." },
  ];

  const mathFeatures = [
    { img: `${IMG_BASE}/math-concepts.png`, title: "Concept mastery", text: "Foundations first, then advanced patterns." },
    { img: `${IMG_BASE}/math-calculator.png`, title: "Calculator strategy", text: "When to use it, when not to, and how to save time." },
    { img: `${IMG_BASE}/math-accuracy.png`, title: "Accuracy + speed", text: "Repeatable routines to reduce silly errors." },
  ];

  const keyQualities = [
    {
      img: `${IMG_BASE}/quality-diagnostic.png`,
      title: "Diagnostic Test",
      text: "We don’t waste time reviewing what students already know. The diagnostic becomes a blueprint for tutoring.",
    },
    {
      img: `${IMG_BASE}/quality-plan.png`,
      title: "Personalized Plan",
      text: "A realistic weekly plan that targets high-yield skills with a timeline for covering all necessary content.",
    },
    {
      img: `${IMG_BASE}/quality-teaching.png`,
      title: "Individualized Teaching",
      text: "One-on-one teaching with DSAT-specific techniques so students understand concepts—not just memorize.",
    },
  ];

  // Results now use 'big' image logic
  const results = [
    { img: `${IMG_BASE}/results-score-real.jpg`, title: "Score Growth", text: "Average gain of 150+ points within 8 weeks of guided practice." },
    { img: `${IMG_BASE}/results-confidence-real.jpg`, title: "Exam Confidence", text: "Students report feeling 'in control' during adaptive modules." },
    { img: `${IMG_BASE}/results-consistency-real.jpg`, title: "Consistency", text: "100% of students completed their customized roadmap on time." },
  ];

  const howLeft = [
    { img: `${IMG_BASE}/step-assessment.png`, title: "Student Assessment", desc: "Diagnostic + goal discovery." },
    { img: `${IMG_BASE}/step-goals.png`, title: "Goal Setting", desc: "Target score + timeline." },
    { img: `${IMG_BASE}/step-plan.png`, title: "Customized Study Plan", desc: "Weekly roadmap + priorities." },
  ];

  const howRight = [
    { img: `${IMG_BASE}/step-sessions.png`, title: "Weekly Sessions", desc: "Instruction + guided practice." },
    { img: `${IMG_BASE}/step-tests.png`, title: "Practice Tests", desc: "Checkpoints to validate progress." },
    { img: `${IMG_BASE}/step-review.png`, title: "Final Review", desc: "Timing, strategy, and final adjustments." },
  ];

  return (
    <div className="satPage">
      <style>{css}</style>

      <PublicHeader />

      {/* HERO */}
      <section className="hero bgGlow">
        <div className="container layer">
          <div className="grid2">
            <div>
              <div className="pill">
                <span className="muted">SAT</span>
                <span className="muted">·</span>
                <span>Digital SAT Focus</span>
              </div>

              <h1 className="heroH1 serif">SAT Tutoring</h1>
              <p className="heroLead">
                Expert, high-yield guidance for students aiming for top programs. We combine diagnostics,
                personalized plans, and 1-on-1 teaching to maximize score growth efficiently.
              </p>

              <div className="btnRow">
                <button className="btn btnPrimary" onClick={() => scrollToId("sat-how")}>
                  Get Started
                </button>
                <button className="btn" onClick={() => scrollToId("sat-curriculum")}>
                  View Curriculum
                </button>
              </div>

            </div>

            <div className="card">
              <div className="heroCardTop">
                <div className="heroTag">
                  Official-style prep <span>DSAT</span>
                </div>
                <div className="pill" style={{ padding: "6px 10px" }}>
                  Structured plan
                </div>
              </div>
              <div className="heroMedia">
                <Img src={`${IMG_BASE}/hero-right.jpg`} alt="SAT tutoring hero visual" className="img" />
                <div className="heroMediaFallback" />
              </div>
            </div>
          </div>

          {/* BENEFITS - NOW USING LARGER GRID & CARDS */}
          <div className="centerBlock" style={{ marginTop: 80 }}>
            <h3 className="uppercase" style={{ fontSize: 30, color: "#020617", fontWeight: 800 }}>Why Choose Us</h3>
          </div>
          <div className="benefits bigGrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {benefits.map((b) => (
              <div className="bigCard" key={b.title}>
                <Img src={b.img} alt={b.title} className="bigIcon" />
                <p className="bigTitle">{b.title}</p>
                <p className="bigText">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get ready… */}
      <section className="section">
        <div className="container">
          <div className="grid2">
            <div>
              <h2 className="serif" style={{ fontSize: 36, margin: 0, color: "#020617", lineHeight: 1.1 }}>
                Get ready for test day by practicing with purpose
              </h2>
              <p className="muted" style={{ marginTop: 14, fontSize: 15, lineHeight: 1.9 }}>
                We build confidence by focusing on fundamentals, pacing, and DSAT-specific strategy.
              </p>
              <ul style={{ marginTop: 12, paddingLeft: 18, color: "#0f172a", lineHeight: 1.9, fontSize: 15 }}>
                <li>Take a diagnostic to identify high-impact gaps</li>
                <li>Follow a weekly personalized roadmap</li>
                <li>Practice with strategy, not just repetition</li>
                <li>Confirm progress with checkpoints and practice tests</li>
              </ul>
            </div>
            <div className="card" style={{ minHeight: 330, display: "flex" }}>
              <Img src={`${IMG_BASE}/sat-student.jpg`} alt="Student studying" className="img" />
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* Inside scoop */}
      <section className="section bgGlow2">
        <div className="container layer">
          <div className="centerBlock">
            <h2 className="serif" style={{ fontSize: 40, margin: 0, color: "#020617" }}>
              The Inside Scoop on the Digital SAT
            </h2>
            <p className="muted" style={{ marginTop: 12, fontSize: 15, lineHeight: 1.9 }}>
              Shorter test, adaptive modules, and different pacing.
            </p>
            <div className="btnRow" style={{ justifyContent: "center" }}>
              <button className="btn" onClick={() => scrollToId("sat-english")}>
                Start with Reading & Writing
              </button>
              <button className="btn" onClick={() => scrollToId("sat-math")}>
                Start with Math
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial + audience */}
      <section className="sectionAlt">
        <div className="container">
          <div className="quote">
            <p className="quoteText serif">
              “I felt way less stressed because I understood exactly what to practice each week — and my timing
              improved the fastest.”
            </p>
            <div className="quoteBy">STUDENT TESTIMONIAL</div>
          </div>

          <div className="audience">
            <div className="audCard">
              <h3 className="audTitle serif">For parents</h3>
              <p className="audText">
                You’ll always know the plan: what your student is working on, why it matters, and how progress is
                measured week to week.
              </p>
              <button className="btn btnPrimary" onClick={() => scrollToId("sat-how")} style={{ alignSelf: "flex-start" }}>
                See the program flow →
              </button>
            </div>

            <div className="audCard">
              <h3 className="audTitle serif">For students</h3>
              <p className="audText">
                Clear steps, targeted practice, and real coaching. We focus on confidence, pacing, and accuracy —
                not just doing more problems.
              </p>
              <button className="btn btnPrimary" onClick={() => scrollToId("sat-curriculum")} style={{ alignSelf: "flex-start" }}>
                View the curriculum →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SAT English */}
      <section className="section" id="sat-english">
        <div className="container">
          <div className="grid2">
            <div>
              <h2 className="serif" style={{ fontSize: 42, margin: 0, color: "#020617" }}>SAT ENGLISH</h2>
              <p className="muted" style={{ marginTop: 12, fontSize: 15, lineHeight: 1.9 }}>
                We build critical reading and persuasive writing skills while training students for DSAT-style patterns and pacing.
              </p>
              <div className="three">
                {englishFeatures.map((f) => (
                  <div className="miniCard" key={f.title}>
                    <Img src={f.img} alt={f.title} className="miniIconImg" />
                    <p className="miniTitle">{f.title}</p>
                    <p className="miniText">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="card" style={{ minHeight: 300 }}>
              <Img src={`${IMG_BASE}/sat-english.jpg`} alt="SAT English" className="img" />
            </div>
          </div>
        </div>
      </section>

      {/* SAT Math */}
      <section className="sectionAlt" id="sat-math">
        <div className="container">
          <div className="grid2">
            <div className="card" style={{ minHeight: 300 }}>
              <Img src={`${IMG_BASE}/sat-math.jpg`} alt="SAT Math" className="img" />
            </div>
            <div>
              <h2 className="serif" style={{ fontSize: 42, margin: 0, color: "#020617" }}>SAT MATH</h2>
              <p className="muted" style={{ marginTop: 12, fontSize: 15, lineHeight: 1.9 }}>
                We build deep conceptual understanding and problem-solving fluency. Students learn to recognize structures.
              </p>
              <div className="three">
                {mathFeatures.map((f) => (
                  <div className="miniCard" key={f.title}>
                    <Img src={f.img} alt={f.title} className="miniIconImg" />
                    <p className="miniTitle">{f.title}</p>
                    <p className="miniText">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KEY QUALITIES - USING LARGER GRID */}
      <section 
        className="section" 
        id="sat-qualities"
        style={{ background: '#f5f4f7' }}
      >
        <div className="container">
          <div className="centerBlock">
            <h2 className="serif uppercase" style={{ fontSize: 40, margin: 0, color: "#020617" }}>Key Qualities</h2>
          </div>
          <div className="bigGrid">
            {keyQualities.map((q) => (
              <div className="bigCard" key={q.title}>
                <Img src={q.img} alt={q.title} className="bigIcon" />
                <p className="bigTitle">{q.title}</p>
                <p className="bigText">{q.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRICULUM - NEW DESIGN */}
      <section
        id="sat-curriculum"
        style={{ 
            background: '#141414', 
            padding: '90px 0' /* <--- ADDED PADDING HERE */
        }} 
      >
        <div className="container">
          <div className="centerBlock">
            <h2 className="serif uppercase" style={{ fontSize: 36, margin: 0, color: "#fff" }}>
              Personalized SAT Success Curriculum
            </h2>
            <p className="muted" style={{ marginTop: 16, fontSize: 18, lineHeight: 1.9, color: '#f5f4f7'}}>
              A comprehensive breakdown of everything we cover to ensure mastery.
            </p>
          </div>

          <div className="currContainer">
            {/* READING & WRITING COLUMN */}
            <div className="currCol">
              <div className="currHeader">READING & WRITING</div>
              {curriculumData.rw.map((sec) => (
                <div key={sec.category} style={{ marginBottom: 24 }}>
                  <div className="currSectionTitle">{sec.category}</div>
                  <ul className="currList">
                    {sec.items.map((item) => (
                      <li key={item} className="currItem">{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* MATH COLUMN */}
            <div className="currCol">
              <div className="currHeader">MATH</div>
              {curriculumData.math.map((sec) => (
                <div key={sec.category} style={{ marginBottom: 24 }}>
                  <div className="currSectionTitle">{sec.category}</div>
                  <ul className="currList">
                    {sec.items.map((item) => (
                      <li key={item} className="currItem">{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How Our Program Works */}
      <section 
        className="section" 
        id="sat-how"
        style={{ background: '#fff' }} 
      >
        <div className="container">
          <div className="centerBlock">
            <h2 className="serif uppercase" style={{ fontSize: 40, margin: 0, color: "#020617" }}>
              How Our Program Works
            </h2>
          </div>

          <div className="timelineWrap">
            <div className="timelineHeader">
              <div>
                <p className="timelineTitle">From Day 1 to Test Day</p>
                <p className="timelineSub">Structured flow, measurable checkpoints, and consistent progress.</p>
              </div>
            </div>

            <div className="timelineGrid">
              <div className="timelineCol">
                <div className="timelineLine" />
                {howLeft.map((s) => (
                  <div className="step" key={s.title}>
                    <div className="dot" />
                    <div className="stepCard">
                      <Img src={s.img} alt={s.title} className="stepIcon" />
                      <div>
                        <p className="stepH">{s.title}</p>
                        <p className="stepD">{s.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="timelineCol">
                <div className="timelineLine" />
                {howRight.map((s) => (
                  <div className="step" key={s.title}>
                    <div className="dot" />
                    <div className="stepCard">
                      <Img src={s.img} alt={s.title} className="stepIcon" />
                      <div>
                        <p className="stepH">{s.title}</p>
                        <p className="stepD">{s.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results - REDESIGNED FOR ACTUAL IMAGES */}
      <section 
        className="sectionAlt"
        style={{ background: '#f5f4f7' }} 
      >
        <div className="container">
          <div className="centerBlock">
            <h2 className="serif uppercase" style={{ fontSize: 40, margin: 0, color: "#020617" }}>
              Student Results
            </h2>
            <p className="muted" style={{ marginTop: 12, fontSize: 16, lineHeight: 1.9 }}>
              Proven outcomes using data-driven strategies.
            </p>
          </div>

          <div className="bigGrid">
            {results.map((r) => (
              <div className="resultCard" key={r.title}>
                <div className="resultImgFrame">
                  <Img src={r.img} alt={r.title} className="resultImg" />
                </div>
                <p className="resultTitle">{r.title}</p>
                <p className="resultText">{r.text}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 40 }} className="quote">
            <p className="quoteText serif">
              “The weekly plan made it obvious what to do. I stopped guessing and started improving.”
            </p>
            <div className="quoteBy">PARENT FEEDBACK</div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <div className="ctaBanner">
            <div className="ctaBg" aria-hidden="true" />
            <div className="ctaInner">
              <h2 className="serif" style={{ fontSize: 36, margin: 0, color: "#020617" }}>
                The best SAT prep is a plan that fits the student.
              </h2>
              <div className="btnRow" style={{ justifyContent: "center" }}>
                <button className="btn btnPrimary" onClick={() => scrollToId("sat-how")}>Let’s go</button>
                <button className="btn" onClick={() => scrollToId("sat-curriculum")}>Curriculum</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}